/* eslint-disable react/display-name */
import type { ReactNode } from "react";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { TriStateCheckbox } from "primereact/tristatecheckbox";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";

export interface SelectOption {
  label: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Filter element factories
//
// Each factory returns a filterElement callback compatible with PrimeReact's
// Column `filterElement` prop. Pass the returned function directly:
//
//   filterElement={textFilterElement("Search users...")}
//   filterElement={multiSelectFilterElement(statusOpts, "Select Status")}
//   filterElement={dropdownFilterElement(typeOpts, "All types")}
//
// For options that depend on runtime state, memoize in the component:
//
//   const statusFilter = useMemo(
//     () => multiSelectFilterElement(statusOptions.map(s => ({ label: s.displayName, value: s.code })), "Select Status"),
//     [statusOptions]
//   );
// ---------------------------------------------------------------------------

/** Returns a text input filter element callback. */
export const textFilterElement =
  (placeholder = "Search...") =>
  (options: { value: string | null; filterApplyCallback: (v: string) => void }): ReactNode => (
    <div className="mt-4">
      <InputText
        value={options.value ?? ""}
        onChange={e => options.filterApplyCallback(e.target.value)}
        placeholder={placeholder}
        className="w-full"
      />
    </div>
  );

/** Returns a MultiSelect chip filter element callback. */
export const multiSelectFilterElement =
  (selectOptions: SelectOption[], placeholder = "Select options", maxSelectedLabels = 1) =>
  (options: { value: string[] | null; filterApplyCallback: (v: string[]) => void }): ReactNode => (
    <div className="mt-4">
      <MultiSelect
        value={Array.isArray(options.value) ? options.value : []}
        options={selectOptions}
        onChange={e => options.filterApplyCallback((e.value ?? []) as string[])}
        placeholder={placeholder}
        className="w-full"
        maxSelectedLabels={maxSelectedLabels}
        display="chip"
      />
    </div>
  );

/**
 * Like multiSelectFilterElement but calls `onFilterChange` directly instead of PrimeReact's
 * filterApplyCallback. Use when the component owns filter state via React's useState setter
 * to bypass PrimeReact's stale-closure bug in onFilterApply.
 */
export const multiSelectOnChangeFilterElement =
  (
    selectOptions: SelectOption[],
    placeholder: string,
    onFilterChange: (value: string[]) => void,
    maxSelectedLabels = 1
  ) =>
  (filterOptions: { value: string[] | null }): ReactNode => (
    <div className="mt-4">
      <MultiSelect
        value={Array.isArray(filterOptions.value) ? filterOptions.value : []}
        options={selectOptions}
        onChange={e => onFilterChange((e.value ?? []) as string[])}
        placeholder={placeholder}
        className="w-full"
        maxSelectedLabels={maxSelectedLabels}
        display="chip"
      />
    </div>
  );

/** Filter element for boolean columns using a TriStateCheckbox (true / false / null). */
export const triStateBooleanFilterElement = (options: {
  value: boolean | null;
  filterApplyCallback: (v: boolean | null) => void;
}): ReactNode => (
  <div className="flex gap-2 items-center mt-4">
    <TriStateCheckbox
      id="tri-state-boolean-filter"
      value={options.value}
      onChange={e => options.filterApplyCallback(e.value ?? null)}
    />
    <label htmlFor="tri-state-boolean-filter" className="whitespace-nowrap">
      <span className="sr-only">Boolean filter (any / true / false)</span>
      <i className="pi pi-check-circle" aria-hidden="true" />
      <span aria-hidden="true"> / </span>
      <i className={`pi pi-times-circle ${themeClasses.iconError}`} aria-hidden="true" />
    </label>
  </div>
);

/** Returns a Dropdown filter element callback with a clear button. */
export const dropdownFilterElement =
  (selectOptions: SelectOption[], placeholder = "Select") =>
  (options: {
    value: string | null;
    filterApplyCallback: (v: string | null) => void;
  }): ReactNode => (
    <div className="mt-4">
      <Dropdown
        value={options.value ?? null}
        options={selectOptions}
        onChange={e => options.filterApplyCallback(e.value as string | null)}
        placeholder={placeholder}
        showClear
        className="w-full"
      />
    </div>
  );
